"use client";

import { signal } from "@preact/signals-react";
import { useSignals } from "@preact/signals-react/runtime";
import { usePathname, useSearchParams } from "next/navigation";
import {
  type ComponentType,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";

import {
  type DialogProps,
  ROUTED_DIALOG_REGISTRY,
  type RoutedDialogName,
} from "@/components/app/dialogs/dialog-registry";
import type {
  RegisterRoutedDialogBeforeCloseHandler,
  RoutedDialogBeforeCloseHandler,
  RoutedDialogCloseReason,
  RoutedDialogOpenChangeHandler,
} from "@/components/app/dialogs/dialog-types";
import { useHistoryManagerRuntime } from "@/hooks/use-history-manager";
import { APP_ROUTES } from "@/utils/constants";
import {
  buildDialogHref,
  DIALOG,
  type DialogHistoryMode,
  type DialogInputParams,
  type DialogName,
  getNextDialogRef,
  parseRoutedDialogState,
  type RoutedDialog,
  type RoutedDialogState,
} from "@/utils/dialog-routes";
import {
  createHistoryEntryId,
  createManagedHistoryEntry,
  findManagedHistoryEntry,
  getCurrentHref,
  getCurrentManagedHistoryEntry,
  historyManagerSignal,
  pushHistoryUrl,
  pushManagedHistoryEntry,
  replaceHistoryUrl,
  replaceManagedHistoryEntry,
} from "@/utils/history-manager";
import { getSameOriginLinkUrlFromEvent } from "@/utils/navigation-events";

export {
  DIALOG,
  type DialogInputParams,
  type DialogName,
  type RoutedDialog,
  type RoutedDialogState,
};
export type {
  RegisterRoutedDialogBeforeCloseHandler,
  RoutedDialogBeforeCloseHandler,
  RoutedDialogCloseReason,
  RoutedDialogOpenChangeHandler,
};

type SetDialogOptions<Name extends RoutedDialogName> =
  | {
      historyMode?: DialogHistoryMode;
      open: true;
      params?: DialogRouteParams;
      props?: DialogProps<Name>;
      replace?: boolean;
      replaceActiveDialog?: boolean;
    }
  | {
      open: false;
    };

export type DialogRouteParams = Record<string, string>;

export type DialogStoreEntry<Name extends RoutedDialogName = RoutedDialogName> =
  {
    href: string;
    historyEntryId?: string;
    historyMode: DialogHistoryMode;
    open: boolean;
    params?: DialogRouteParams;
    props?: DialogProps<Name>;
    ref: number;
  };

export type DialogStore = Partial<{
  [Name in RoutedDialogName]: DialogStoreEntry<Name>;
}>;

export const dialogStoreSignal = signal<DialogStore>({});
const dialogBeforeCloseHandlers = new Map<
  RoutedDialogName,
  RoutedDialogBeforeCloseHandler
>();
const dialogRuntimePropsCache = new Map<
  string,
  DialogProps<RoutedDialogName> | undefined
>();
// Blocks dialog-local URL sync while a same-origin link is leaving /dialog/*.
let pendingNonDialogNavigationHref: string | null = null;

type ActiveDialogState = RoutedDialog & {
  historyEntryId?: string;
  historyMode: DialogHistoryMode;
};

type ResolvedDialogState = {
  activeDialog: ActiveDialogState | null;
  href: string | null;
};

const getOpenDialogRefs = (dialogStore: DialogStore) => {
  return Object.values(dialogStore).flatMap((dialogEntry) =>
    dialogEntry?.open ? [{ ref: dialogEntry.ref }] : [],
  );
};

const getOpenDialogsFromStore = (
  dialogStore: DialogStore,
): ActiveDialogState[] => {
  return Object.entries(dialogStore)
    .flatMap(([dialog, dialogEntry]) => {
      if (!dialogEntry?.open) {
        return [];
      }

      return [
        {
          dialog: dialog as DialogName,
          href: dialogEntry.href,
          historyEntryId: dialogEntry.historyEntryId,
          historyMode: dialogEntry.historyMode,
          params: (dialogEntry.params ?? {}) as Record<string, string>,
        },
      ];
    })
    .sort((left, right) => {
      const leftRef =
        dialogStore[left.dialog as RoutedDialogName]?.ref ??
        Number.MIN_SAFE_INTEGER;
      const rightRef =
        dialogStore[right.dialog as RoutedDialogName]?.ref ??
        Number.MIN_SAFE_INTEGER;

      return leftRef - rightRef;
    });
};

const getTopDialogFromStore = (dialogStore: DialogStore) => {
  return getOpenDialogsFromStore(dialogStore).at(-1) ?? null;
};

const setDialogBeforeCloseHandler = (
  dialog: RoutedDialogName,
  guard: RoutedDialogBeforeCloseHandler | null,
) => {
  if (guard) {
    dialogBeforeCloseHandlers.set(dialog, guard);
    return;
  }

  dialogBeforeCloseHandlers.delete(dialog);
};

export const useRegisterDialogBeforeClose = (
  dialog: RoutedDialogName,
  guard: RoutedDialogBeforeCloseHandler | null,
) => {
  useEffect(() => {
    if (!guard) {
      return;
    }

    setDialogBeforeCloseHandler(dialog, guard);

    return () => {
      if (dialogBeforeCloseHandlers.get(dialog) === guard) {
        setDialogBeforeCloseHandler(dialog, null);
      }
    };
  }, [dialog, guard]);
};

const runDialogBeforeClose = async (
  dialog: RoutedDialogName,
  reason: RoutedDialogCloseReason,
) => {
  const guard = dialogBeforeCloseHandlers.get(dialog);

  if (!guard) {
    return true;
  }

  return (await guard(reason)) !== false;
};

const runDialogOnClose = (
  dialog: RoutedDialogName,
  dialogStore: DialogStore = dialogStoreSignal.value,
) => {
  const onClose = (
    dialogStore[dialog]?.props as { onClose?: (() => void) | undefined }
  )?.onClose;

  if (typeof onClose === "function") {
    onClose();
  }
};

const reconcileDialogStore = (
  routeState: ResolvedDialogState,
  previousStore: DialogStore,
): DialogStore => {
  if (!routeState.activeDialog) {
    return {};
  }

  const routeDialogName = routeState.activeDialog.dialog as RoutedDialogName;
  const existingEntry = previousStore[routeDialogName];

  if (existingEntry?.open) {
    return Object.entries(previousStore).reduce<DialogStore>(
      (nextStore, [dialog, dialogEntry]) => {
        if (!dialogEntry?.open || dialogEntry.ref > existingEntry.ref) {
          return nextStore;
        }

        const nextDialogEntry = (
          dialog === routeDialogName
            ? {
                ...dialogEntry,
                historyEntryId:
                  routeState.activeDialog?.historyEntryId ??
                  dialogEntry.historyEntryId,
                historyMode:
                  routeState.activeDialog?.historyMode ??
                  dialogEntry.historyMode,
                href: routeState.activeDialog?.href ?? dialogEntry.href,
                params: routeState.activeDialog?.params ?? dialogEntry.params,
              }
            : dialogEntry
        ) as DialogStoreEntry;

        (nextStore as Partial<Record<RoutedDialogName, DialogStoreEntry>>)[
          dialog as RoutedDialogName
        ] = nextDialogEntry;

        return nextStore;
      },
      {},
    );
  }

  return {
    ...previousStore,
    [routeDialogName]: {
      href: routeState.activeDialog.href,
      historyEntryId: routeState.activeDialog.historyEntryId,
      historyMode: routeState.activeDialog.historyMode,
      open: true,
      params: routeState.activeDialog.params,
      props:
        previousStore[routeDialogName]?.props ??
        (routeState.activeDialog.historyEntryId
          ? dialogRuntimePropsCache.get(routeState.activeDialog.historyEntryId)
          : undefined),
      ref: getNextDialogRef(getOpenDialogRefs(previousStore)),
    },
  };
};

const resolveDialogState = ({
  pathname,
  search,
  historyState,
}: {
  historyState: unknown;
  pathname: string;
  search: string;
}): ResolvedDialogState => {
  const routeState = parseRoutedDialogState({
    pathname,
    search,
  });
  const entryDialogState = findManagedHistoryEntry({
    historyState,
    predicate: (entry) =>
      entry.kind === "dialog" && entry.name in ROUTED_DIALOG_REGISTRY,
  });

  if (entryDialogState) {
    return {
      activeDialog: {
        dialog: entryDialogState.name as RoutedDialogName,
        historyEntryId: entryDialogState.entryId,
        historyMode: "entry",
        href: entryDialogState.href,
        params: entryDialogState.params,
      },
      href: entryDialogState.href,
    };
  }

  if (!routeState.activeDialog) {
    return {
      activeDialog: null,
      href: null,
    };
  }

  return {
    activeDialog: {
      ...routeState.activeDialog,
      historyMode: "route",
    },
    href: routeState.href,
  };
};

const pushRouteDialogHistoryEntry = (href: string) => {
  pushHistoryUrl(href);
};

const replaceRouteDialogHistoryEntry = (href: string) => {
  replaceHistoryUrl(href);
};

export const useDialogState = <Name extends RoutedDialogName>(dialog: Name) => {
  return dialogStoreSignal.value[dialog] as DialogStoreEntry<Name> | undefined;
};

export const useStableSearchParams = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const initialPathnameRef = useRef<string | null>(null);
  const stableSearchRef = useRef("");

  if (initialPathnameRef.current === null) {
    initialPathnameRef.current = pathname;
    stableSearchRef.current = searchParams.toString();
  }

  if (pathname === initialPathnameRef.current) {
    stableSearchRef.current = searchParams.toString();
  }

  return useMemo(() => {
    const nextSearch =
      pathname === initialPathnameRef.current
        ? searchParams.toString()
        : stableSearchRef.current;

    return new URLSearchParams(nextSearch);
  }, [pathname, searchParams]);
};

export const waitForNextPaint = () => {
  return new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        resolve();
      });
    });
  });
};

const getCurrentResolvedDialogState = () => {
  return resolveDialogState({
    historyState: window.history.state,
    pathname: window.location.pathname,
    search: window.location.search.replace(/^\?/, ""),
  });
};

const queueNoOpBackFallback = (closeHref: string) => {
  let fallbackTimeout: number | null = null;
  let settled = false;

  const cleanup = () => {
    if (fallbackTimeout !== null) {
      window.clearTimeout(fallbackTimeout);
    }

    window.removeEventListener("popstate", handlePopState);
  };

  const fallbackIfStillOnDialog = () => {
    if (settled) {
      return;
    }

    settled = true;
    cleanup();

    if (getCurrentHref() !== closeHref) {
      return;
    }

    replaceHistoryUrl(APP_ROUTES.app);
    dialogStoreSignal.value = reconcileDialogStore(
      getCurrentResolvedDialogState(),
      dialogStoreSignal.value,
    );
  };

  const handlePopState = () => {
    if (fallbackTimeout !== null) {
      window.clearTimeout(fallbackTimeout);
    }

    fallbackTimeout = window.setTimeout(fallbackIfStillOnDialog, 0);
  };

  window.addEventListener("popstate", handlePopState, { once: true });
  fallbackTimeout = window.setTimeout(fallbackIfStillOnDialog, 250);
};

const requestDialogClose = (dialog: RoutedDialogName) => {
  const currentStore = dialogStoreSignal.value;
  const dialogEntry = currentStore[dialog];
  const activeDialog = getTopDialogFromStore(currentStore);

  if (!dialogEntry?.open || activeDialog?.dialog !== dialog) {
    return false;
  }

  runDialogOnClose(dialog, currentStore);

  dialogStoreSignal.value = {
    ...currentStore,
    [dialog]: {
      ...dialogEntry,
      open: false,
    },
  };

  if (typeof window !== "undefined") {
    const closeHref = getCurrentHref();
    const shouldFallbackOnNoOpBack =
      dialogEntry.historyMode === "route" &&
      !!closeHref &&
      closeHref === dialogEntry.href;

    if (shouldFallbackOnNoOpBack) {
      queueNoOpBackFallback(closeHref);
    }

    window.history.back();
  }

  return true;
};

export const closeDialog = () => {
  const activeDialog = getTopDialogFromStore(dialogStoreSignal.value);

  if (!activeDialog) {
    return false;
  }

  return requestDialogClose(activeDialog.dialog as RoutedDialogName);
};

const dismissOpenDialogsForRouteNavigation = () => {
  const currentStore = dialogStoreSignal.value;
  const openDialogs = getOpenDialogsFromStore(currentStore);

  if (!openDialogs.length) {
    return false;
  }

  openDialogs.forEach((dialog) => {
    runDialogOnClose(dialog.dialog as RoutedDialogName, currentStore);
  });

  dialogStoreSignal.value = {};

  return true;
};

export const setDialog = <Name extends RoutedDialogName>(
  dialog: Name,
  options: SetDialogOptions<Name>,
) => {
  if (!options.open) {
    requestDialogClose(dialog);
    return;
  }

  const currentStore = dialogStoreSignal.value;
  const openDialogs = getOpenDialogsFromStore(currentStore);
  const activeDialog = openDialogs.at(-1);
  const activeManagedEntry = getCurrentManagedHistoryEntry();
  const historyMode = options.historyMode ?? "route";
  const isSameActiveDialog = activeDialog?.dialog === dialog;
  const shouldReplaceSameEntryDialog =
    historyMode === "entry" &&
    isSameActiveDialog &&
    activeDialog.historyMode === "entry";
  const shouldReplaceSameRouteDialog =
    historyMode === "route" &&
    !!options.replace &&
    isSameActiveDialog &&
    activeDialog.historyMode === "route";
  const shouldReplaceActiveManagedDialog =
    !!options.replace &&
    !!activeDialog &&
    !!activeManagedEntry &&
    activeManagedEntry.kind === "dialog" &&
    activeManagedEntry.entryId === activeDialog.historyEntryId;
  const shouldReplaceLocalManagedEntry =
    !!options.replace &&
    !!activeManagedEntry &&
    activeManagedEntry.kind !== "dialog";
  const shouldReplaceDifferentActiveDialog =
    !!options.replaceActiveDialog &&
    !!activeDialog &&
    (!activeManagedEntry ||
      (activeManagedEntry.kind === "dialog" &&
        activeManagedEntry.entryId === activeDialog.historyEntryId));
  const shouldReplaceHistoryEntry =
    shouldReplaceSameEntryDialog ||
    shouldReplaceSameRouteDialog ||
    shouldReplaceActiveManagedDialog ||
    shouldReplaceLocalManagedEntry ||
    shouldReplaceDifferentActiveDialog;
  const shouldReplaceActiveDialog =
    shouldReplaceSameEntryDialog ||
    shouldReplaceSameRouteDialog ||
    shouldReplaceActiveManagedDialog ||
    shouldReplaceDifferentActiveDialog;
  const shouldReuseDialogEntry = isSameActiveDialog;
  const replacedDialog = shouldReplaceActiveDialog
    ? (activeDialog.dialog as RoutedDialogName)
    : null;
  const nextRef = shouldReplaceActiveDialog
    ? (currentStore[activeDialog.dialog as RoutedDialogName]?.ref ??
      getNextDialogRef([]))
    : shouldReuseDialogEntry
      ? (currentStore[dialog]?.ref ?? getNextDialogRef([]))
      : getNextDialogRef(getOpenDialogRefs(currentStore));
  const nextHref =
    historyMode === "entry"
      ? getCurrentHref()
      : buildDialogHref({
          dialog,
          params: options.params as DialogInputParams | undefined,
        });
  const nextHistoryEntryId =
    historyMode === "entry"
      ? shouldReplaceActiveDialog && activeManagedEntry?.kind === "dialog"
        ? activeManagedEntry.entryId
        : createHistoryEntryId()
      : undefined;
  const nextProps = options.props ?? currentStore[dialog]?.props;

  if (!nextHref) {
    return;
  }

  if (historyMode === "route" && pendingNonDialogNavigationHref) {
    return;
  }

  if (nextHistoryEntryId) {
    dialogRuntimePropsCache.set(
      nextHistoryEntryId,
      nextProps as DialogProps<RoutedDialogName> | undefined,
    );
  }

  if (replacedDialog && replacedDialog !== dialog) {
    runDialogOnClose(replacedDialog, currentStore);
  }

  dialogStoreSignal.value = {
    ...currentStore,
    ...(replacedDialog && replacedDialog !== dialog
      ? {
          [replacedDialog]: {
            ...currentStore[replacedDialog],
            open: false,
          },
        }
      : {}),
    [dialog]: {
      href: nextHref,
      historyEntryId: nextHistoryEntryId,
      historyMode,
      open: true,
      params: options.params,
      props: nextProps,
      ref: nextRef,
    },
  };

  if (historyMode === "entry") {
    const historyDialogState = createManagedHistoryEntry({
      entryId: nextHistoryEntryId!,
      href: nextHref,
      kind: "dialog",
      name: dialog,
      params: options.params,
    });

    if (!historyDialogState) {
      return;
    }

    if (shouldReplaceHistoryEntry) {
      replaceManagedHistoryEntry(historyDialogState);
      return;
    }

    pushManagedHistoryEntry(historyDialogState);
    return;
  }

  if (shouldReplaceHistoryEntry) {
    replaceRouteDialogHistoryEntry(nextHref);
    return;
  }

  pushRouteDialogHistoryEntry(nextHref);
};

export const useRoutedDialogs = () => {
  return {
    closeDialog,
    DIALOG,
    setDialog,
  };
};

const renderDialog = (dialog: RoutedDialogName, dialogStore: DialogStore) => {
  const Component = ROUTED_DIALOG_REGISTRY[dialog] as ComponentType<
    Record<string, unknown>
  >;
  const dialogState = dialogStore[dialog];
  const userOnOpenChange = dialogState?.props?.onOpenChange as
    | RoutedDialogOpenChangeHandler
    | undefined;
  const registerBeforeClose: RegisterRoutedDialogBeforeCloseHandler = (
    guard,
  ) => {
    setDialogBeforeCloseHandler(dialog, guard);

    return () => {
      if (dialogBeforeCloseHandlers.get(dialog) === guard) {
        setDialogBeforeCloseHandler(dialog, null);
      }
    };
  };
  const onOpenChange: RoutedDialogOpenChangeHandler = async (open) => {
    if (open) {
      return userOnOpenChange?.(open);
    }

    if (getTopDialogFromStore(dialogStoreSignal.value)?.dialog !== dialog) {
      return false;
    }

    const allowed = await runDialogBeforeClose(dialog, "programmatic");

    if (!allowed) {
      return false;
    }

    const result = await userOnOpenChange?.(open);

    if (result === false) {
      return result;
    }

    return requestDialogClose(dialog) ? result : false;
  };

  return (
    <Component
      {...(dialogState?.params ?? {})}
      {...dialogState?.props}
      dialogName={dialog}
      key={dialog}
      onOpenChange={onOpenChange}
      open={Boolean(dialogState?.open)}
      registerBeforeClose={registerBeforeClose}
    />
  );
};

export const DialogProvider = () => {
  useSignals();
  useHistoryManagerRuntime();
  const pendingPopCloseRef = useRef(false);
  const pendingRouteStateRef = useRef<ResolvedDialogState | null>(null);

  const pathname = usePathname();
  const searchParams = useSearchParams();
  const dialogStore = dialogStoreSignal.value;
  const historyRevision = historyManagerSignal.value.revision;
  const search = searchParams.toString();
  const browserPathname =
    typeof window === "undefined" ? pathname : window.location.pathname;
  const browserSearch =
    typeof window === "undefined"
      ? search
      : window.location.search.replace(/^\?/, "");

  const routeState = useMemo(() => {
    return resolveDialogState({
      historyState: typeof window === "undefined" ? null : window.history.state,
      pathname: browserPathname,
      search: browserSearch,
    });
  }, [browserPathname, browserSearch, historyRevision]);
  const dialogNames = Object.keys(ROUTED_DIALOG_REGISTRY) as RoutedDialogName[];

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (!getTopDialogFromStore(dialogStoreSignal.value)) {
        return;
      }

      const url = getSameOriginLinkUrlFromEvent(event);

      if (!url) {
        return;
      }

      const targetDialogState = parseRoutedDialogState({
        pathname: url.pathname,
        search: url.search.replace(/^\?/, ""),
      });

      if (targetDialogState.activeDialog) {
        return;
      }

      const targetHref = `${url.pathname}${url.search}`;

      pendingNonDialogNavigationHref = targetHref;

      window.setTimeout(() => {
        if (pendingNonDialogNavigationHref !== targetHref) {
          return;
        }

        dismissOpenDialogsForRouteNavigation();
      }, 0);
    };

    document.addEventListener("click", handleDocumentClick, {
      capture: true,
    });

    return () => {
      document.removeEventListener("click", handleDocumentClick, {
        capture: true,
      });
    };
  }, []);

  useLayoutEffect(() => {
    pendingRouteStateRef.current = routeState;

    if (pendingPopCloseRef.current) {
      return;
    }

    const currentStore = dialogStoreSignal.value;
    const activeDialog = getTopDialogFromStore(currentStore);

    if (pendingNonDialogNavigationHref) {
      if (routeState.activeDialog) {
        return;
      }

      pendingNonDialogNavigationHref = null;
    }

    if (
      activeDialog &&
      activeDialog.dialog in ROUTED_DIALOG_REGISTRY &&
      (!routeState.activeDialog ||
        activeDialog.dialog !== routeState.activeDialog.dialog ||
        activeDialog.historyMode !== routeState.activeDialog.historyMode ||
        activeDialog.historyEntryId !==
          routeState.activeDialog.historyEntryId ||
        activeDialog.href !== routeState.activeDialog.href)
    ) {
      const dialog = activeDialog.dialog as RoutedDialogName;
      pendingPopCloseRef.current = true;

      void (async () => {
        const allowed = await runDialogBeforeClose(dialog, "browser-back");
        const pendingRouteState = pendingRouteStateRef.current ?? routeState;
        pendingRouteStateRef.current = null;
        pendingPopCloseRef.current = false;

        if (allowed) {
          runDialogOnClose(dialog, dialogStoreSignal.value);
          dialogStoreSignal.value = reconcileDialogStore(
            pendingRouteState,
            dialogStoreSignal.value,
          );
          return;
        }

        if (typeof window !== "undefined") {
          window.history.forward();
        }
      })();

      return;
    }

    dialogStoreSignal.value = reconcileDialogStore(routeState, currentStore);
  }, [routeState]);

  return <>{dialogNames.map((dialog) => renderDialog(dialog, dialogStore))}</>;
};
